import { FileUploadResponse } from '../../../../api/core'
import {
  EditGameFormValues,
  GameAnalystMap,
  GameMarketRef,
  GamePosition,
  GameStoreRef,
  InternalGameDocument,
  InternalGameMutation,
} from '../types/InternalGame'

export const gameStages = [
  { id: 1, labelKey: 'internal-analysts:games_stage_concept' },
  { id: 2, labelKey: 'internal-analysts:games_stage_prototype' },
  { id: 3, labelKey: 'internal-analysts:games_stage_alpha' },
  { id: 4, labelKey: 'internal-analysts:games_stage_beta' },
  { id: 5, labelKey: 'internal-analysts:games_stage_soft_launch' },
  { id: 6, labelKey: 'internal-analysts:games_stage_global_launch' },
  { id: 7, labelKey: 'internal-analysts:games_stage_update' },
] as const
export const softLaunchStageId = 5

// iOS and Android aren't offered; a save keeps them as stored.
export const visiblePlatforms = [
  { id: 'PC', label: 'PC' },
  { id: 'XBOX', label: 'Xbox' },
  { id: 'PLAYSTATION', label: 'PlayStation' },
  { id: 'NINTENDO_SWITCH', label: 'Nintendo Switch' },
] as const
const visiblePlatformIds: string[] = visiblePlatforms.map(({ id }) => id)

export const genreInnovatorOptions = [
  { value: 0, labelKey: 'internal-analysts:games_importance_none' },
  { value: 1, labelKey: 'internal-analysts:games_importance_very_low' },
  { value: 2, labelKey: 'internal-analysts:games_importance_low' },
  { value: 3, labelKey: 'internal-analysts:games_importance_medium' },
  { value: 4, labelKey: 'internal-analysts:games_importance_high' },
  { value: 5, labelKey: 'internal-analysts:games_importance_very_high' },
] as const

const positionId = (storeId: string, marketId: string) => `${storeId}-${marketId}`

export const buildPositions = (stores: GameStoreRef[], markets: GameMarketRef[]): GamePosition[] =>
  stores.flatMap((store) =>
    markets.map((market) => ({
      id: positionId(store.id, market.id),
      storeId: store.id,
      storeName: store.name ?? '',
      marketId: market.id,
      marketIso: market.iso ?? '',
      marketName: market.name ?? '',
      fullName: `${store.name ?? ''} ${market.name ?? ''}`,
    }))
  )

const byFullName = (a: GamePosition, b: GamePosition) => a.fullName.localeCompare(b.fullName)

// Every store × market, plus the game's own positions whose store or market isn't listed anymore, so a save keeps them.
export const getDialogPositions = (stores: GameStoreRef[], markets: GameMarketRef[], game?: Pick<InternalGameDocument, 'stores' | 'markets'>) => {
  const positions = buildPositions(stores, markets)
  const known = new Set(positions.map(({ id }) => id))
  const missing = game ? buildPositions(game.stores ?? [], game.markets ?? []).filter(({ id }) => !known.has(id)) : []
  return [...positions, ...missing].sort(byFullName)
}

export type SubgenreGroup = { genre: string; names: string[] }

// Legacy's `group by parentGenre`, sorted by genre and name. A stored name that isn't listed is kept in its own group.
export const getSubgenreGroups = (subgenres: { name: string; parentGenre?: string }[], storedName: string, unknownGroupLabel: string): SubgenreGroup[] => {
  const groups = new Map<string, string[]>()
  ;[...subgenres]
    .sort((a, b) => (a.parentGenre ?? '').localeCompare(b.parentGenre ?? '') || a.name.localeCompare(b.name))
    .forEach(({ name, parentGenre }) => groups.set(parentGenre ?? '', [...(groups.get(parentGenre ?? '') ?? []), name]))
  const result = Array.from(groups, ([genre, names]) => ({ genre, names }))
  return storedName && !subgenres.some(({ name }) => name === storedName) ? [...result, { genre: unknownGroupLabel, names: [storedName] }] : result
}

export type SoftLaunchMarketOption = { iso: string; name: string }

// Every position's market once, by name, as legacy. A stored iso that no position has is kept.
export const getSoftLaunchMarketOptions = (positions: GamePosition[], storedIso: string): SoftLaunchMarketOption[] => {
  const byIso = new Map<string, string>()
  positions.forEach(({ marketIso, marketName }) => marketIso && !byIso.has(marketIso) && byIso.set(marketIso, marketName || marketIso))
  if (storedIso && !byIso.has(storedIso)) byIso.set(storedIso, storedIso)
  return Array.from(byIso, ([iso, name]) => ({ iso, name })).sort((a, b) => a.name.localeCompare(b.name))
}

export const getGamePositionIds = (game: Pick<InternalGameDocument, 'stores' | 'markets'>) =>
  (game.stores ?? []).flatMap((store) => (game.markets ?? []).map((market) => positionId(store.id, market.id)))

export const addPositionId = (positionIds: string[], id: string, positions: GamePosition[]) => {
  const names = new Map(positions.map((position) => [position.id, position.fullName]))
  return [...positionIds, id].sort((a, b) => (names.get(a) ?? a).localeCompare(names.get(b) ?? b))
}

export const toEditGameFormValues = (game: InternalGameDocument | undefined, analystMap: GameAnalystMap): EditGameFormValues => {
  if (!game) {
    return {
      name: '',
      desc: '',
      icon: null,
      artist: '',
      appId: '',
      analystId: '',
      platforms: [],
      conventionalSubgenre: '',
      overrideConventionals: false,
      genreInnovator: '',
      archive: true,
      stageId: 6,
      softLaunchMarket: '',
      positionIds: [],
    }
  }
  const appId = game.appId === null || game.appId === undefined ? '' : String(game.appId)
  return {
    name: game.name ?? '',
    desc: game.desc ?? '',
    icon: game.icon ?? null,
    artist: game.artist ?? '',
    appId,
    analystId: (appId && analystMap[appId]) || '',
    platforms: (game.platforms ?? []).filter((platform) => visiblePlatformIds.includes(platform)),
    conventionalSubgenre: game.conventionalSubgenre ?? '',
    overrideConventionals: !!game.overrideConventionals,
    genreInnovator: game.genreInnovator ?? '',
    archive: !!game.archive,
    stageId: game.stageId ?? '',
    softLaunchMarket: game.softLaunchMarket ?? '',
    positionIds: getGamePositionIds(game),
  }
}

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

const toStoreAndMarketIds = (positionIds: string[], positions: GamePosition[]) => {
  const byId = new Map(positions.map((position) => [position.id, position]))
  const selected = positionIds.map((id) => byId.get(id)).filter((position): position is GamePosition => !!position)
  return {
    stores: Array.from(new Set(selected.map(({ storeId }) => storeId))),
    markets: Array.from(new Set(selected.map(({ marketId }) => marketId))),
  }
}

type EditableField = Exclude<keyof EditGameFormValues, 'analystId' | 'positionIds'>

const toStoredValue = (field: EditableField, values: EditGameFormValues, storedPlatforms: string[]): unknown => {
  switch (field) {
    case 'appId':
      return values.appId.trim() === '' ? null : Number(values.appId)
    case 'platforms':
      return [...storedPlatforms.filter((platform) => !visiblePlatformIds.includes(platform)), ...values.platforms]
    case 'conventionalSubgenre':
    case 'softLaunchMarket':
      return values[field] || null
    case 'genreInnovator':
    case 'stageId':
      return values[field] === '' ? null : values[field]
    default:
      return values[field]
  }
}

const editableFields: EditableField[] = [
  'name',
  'desc',
  'icon',
  'artist',
  'appId',
  'platforms',
  'conventionalSubgenre',
  'overrideConventionals',
  'genreInnovator',
  'archive',
  'stageId',
  'softLaunchMarket',
]

// An edit sends the stored document with only the changed fields replaced, so an unchanged save sends it back as stored.
export const toGameMutation = (stored: InternalGameDocument | undefined, values: EditGameFormValues, positions: GamePosition[]): InternalGameMutation => {
  const positionLists = toStoreAndMarketIds(values.positionIds, positions)
  if (!stored) {
    const optional: Partial<InternalGameMutation> = {}
    if (values.icon) optional.icon = values.icon
    if (values.artist) optional.artist = values.artist
    if (values.conventionalSubgenre) optional.conventionalSubgenre = values.conventionalSubgenre
    if (values.overrideConventionals) optional.overrideConventionals = true
    if (values.genreInnovator !== '') optional.genreInnovator = values.genreInnovator
    if (values.softLaunchMarket) optional.softLaunchMarket = values.softLaunchMarket
    return {
      archive: values.archive,
      stageId: values.stageId === '' ? null : values.stageId,
      name: values.name,
      desc: values.desc,
      appId: toStoredValue('appId', values, []) as number | null,
      ...positionLists,
      platforms: values.platforms,
      ...optional,
    }
  }
  const initial = toEditGameFormValues(stored, {})
  const storedPlatforms = stored.platforms ?? []
  const changed = Object.fromEntries(
    editableFields.filter((field) => !sameValue(values[field], initial[field])).map((field) => [field, toStoredValue(field, values, storedPlatforms)])
  )
  const positionsChanged = !sameValue(values.positionIds, initial.positionIds)
  const storedLists = toStoreAndMarketIds(initial.positionIds, buildPositions(stored.stores ?? [], stored.markets ?? []))
  return { ...stored, ...changed, ...(positionsChanged ? positionLists : storedLists) }
}

export const setAppAnalyst = (map: GameAnalystMap, appId: string, analystId: string): GameAnalystMap => {
  const next = { ...map }
  if (analystId) next[appId] = analystId
  else delete next[appId]
  return next
}

export type GameFieldKey = EditableField | 'stores' | 'markets'

const isEmpty = (value: unknown) => value === null || value === undefined || value === ''
const iconUrl = (icon: unknown) => (typeof icon === 'string' ? icon : (icon as FileUploadResponse | null | undefined)?.url ?? null)
const sortedIds = (values: unknown) =>
  ((values as (string | { id: string })[] | null | undefined) ?? []).map((value) => (typeof value === 'string' ? value : value.id)).sort()

const checkedFields: GameFieldKey[] = [...editableFields, 'stores', 'markets']

const fieldValue = (field: GameFieldKey, value: unknown) => {
  if (field === 'icon') return iconUrl(value)
  if (field === 'stores' || field === 'markets') return sortedIds(value)
  if (field === 'platforms') return [...((value as string[] | null | undefined) ?? [])].sort()
  if (field === 'overrideConventionals' || field === 'archive') return !!value
  return isEmpty(value) ? null : value
}

// The fields `sent` has that the stored game (a fresh read) doesn't match.
export const getUnstoredGameFields = (sent: Partial<InternalGameMutation>, stored: InternalGameDocument): GameFieldKey[] =>
  checkedFields.filter((field) => field in sent && !sameValue(fieldValue(field, sent[field]), fieldValue(field, stored[field])))

export const gameFieldLabelKeys: Record<GameFieldKey, string> = {
  name: 'internal-analysts:games_name',
  desc: 'internal-analysts:games_description',
  icon: 'internal-analysts:games_icon',
  artist: 'internal-analysts:games_artist',
  appId: 'internal-analysts:games_app_id',
  platforms: 'internal-analysts:games_platforms',
  conventionalSubgenre: 'internal-analysts:games_subgenre',
  overrideConventionals: 'internal-analysts:games_override_subgenre',
  genreInnovator: 'internal-analysts:games_importance',
  archive: 'internal-analysts:games_archive',
  stageId: 'internal-analysts:games_stage',
  softLaunchMarket: 'internal-analysts:games_soft_launch_market',
  stores: 'internal-analysts:games_positions',
  markets: 'internal-analysts:games_positions',
}
